import Navbar from "~/components/navbar";
import { Avatar, Button, Card, CardBody, Link } from "@nextui-org/react";
import { MetaFunction } from "react-router";
import { IoLogoGithub, IoLogoLinkedin, IoLogoTwitter } from "react-icons/io5";
import { LuArrowRight, LuExternalLink, LuSearch } from "react-icons/lu";

import { HiSparkles } from "react-icons/hi2";
import { TbBolt } from "react-icons/tb";
import DotPattern from "~/components/dot-pattern";
import { getPublicAssetURL } from "~/utils/assets";
import { OG_METADATA } from "~/utils/constants";

export const meta: MetaFunction = () => {
  return [
    {
      title: "About Us",
    },
    {
      name: "description",
      content:
        "Learn about tribe.best, a next-generation service discovery platform connecting quality-conscious consumers with India's finest service experts through an intelligent, AI-powered marketplace.",
    },
    ...OG_METADATA,
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "tribe.best",
        url: "https://tribe.best",
        logo: "https://tribe.best/apple-touch-icon.png",
        email: "hello@tribe.best",
        description:
          "tribe.best is a next-generation service discovery platform that connects quality-conscious consumers with India's finest service experts through an intelligent, AI-powered marketplace.",
        foundingDate: "2023",
        telephone: "+916360004054",
        contactPoint: [
          {
            "@type": "ContactPoint",
            email: "hello@tribe.best",
            telephone: "+916360004054",
          },
        ],
        legalName: "Speakleap Technologies Private Limited",
        numberOfEmployees: {
          "@type": "QuantitativeValue",
          value: 4,
        },
        address: [
          {
            "@type": "PostalAddress",
            streetAddress:
              "1st floor, SPD Plaza, 52, Jyoti Niwas College Rd, Koramangala",
            addressLocality: "Bengaluru",
            addressRegion: "Karnataka",
            postalCode: "560034",
            addressCountry: "India",
          },
        ],
      },
    },
  ];
};

const teamMembers = [
  {
    name: "Ugam Kamat",
    position: "Co-Founder / CEO",
    image: getPublicAssetURL("assets/about/ugam.jpeg"),
    socials: {
      linkedIn: "https://www.linkedin.com/in/ugamkamat/",
      twitter: "https://x.com/ugamkamat",
    },
  },
  {
    name: "Kushank Poddar",
    position: "Co-Founder / COO",
    image: getPublicAssetURL("assets/about/kushank.jpeg"),
    socials: {
      linkedIn: "https://www.linkedin.com/in/kushank-poddar/",
      twitter: "https://x.com/kushankpoddar",
    },
  },
  {
    name: "Lavish Thakkar",
    position: "Co-Founder / CTO",
    image: getPublicAssetURL("assets/about/lavish.jpeg"),
    socials: {
      linkedIn: "https://www.linkedin.com/in/lavisht22/",
      twitter: "https://x.com/lthakkar22",
      github: "https://github.com/lavisht22",
    },
  },
  {
    name: "Priyanshu Mishra",
    position: "Founding Engineer",
    image: getPublicAssetURL("assets/about/priyanshu.jpeg"),
    socials: {
      linkedIn: "https://www.linkedin.com/in/priyansmishra/",
      github: "https://github.com/baffle97",
    },
  },
];

const becomePart = [
  {
    title: "Book",
    description:
      "Find the perfect expert for your needs and book them directly through our platform.",
    image: getPublicAssetURL("assets/about/card1.webp"),
    buttonText: "Explore experts",
    url: "/",
    external: false,
  },
  {
    title: "Work",
    description:
      "We’re a tight-knit team with a shared vision and a high-output work ethic.",
    image: getPublicAssetURL("assets/about/card2.webp"),
    buttonText: "Explore open positions",
    url: "https://www.linkedin.com/company/tribe-best/jobs",
    external: true,
  },
  {
    title: "Partner",
    description:
      "Join our curated community of top professionals and let AI bring your ideal clients directly to you.",
    image: getPublicAssetURL("assets/about/card3.webp"),
    buttonText: "Become an expert",
    url: "/become-expert",
    external: false,
  },
];

export default function AboutUs() {
  return (
    <div className="w-full mx-auto">
      <Navbar />
      <section className="w-full relative">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 flex flex-col gap-8 md:gap-48">
          <div className="text-center z-10">
            <h2 className="text-5xl md:text-7xl font-bold text-foreground-100 mb-6 animate-fade-up animate-delay-500">
              Find India&apos;s finest experts
            </h2>
            <p className="text-xl md:text-2xl text-foreground-200 max-w-2xl mx-auto animate-fade-up animate-delay-[1200ms]">
              tribe.best connects you with exceptional professionals who turn
              vision into reality - from award-winning photographers to
              visionary interior designers.
            </p>
            <Button
              as={Link}
              href="/"
              className="mt-8 animate-fade-up animate-delay-[1200ms]"
              endContent={<LuArrowRight className="size-4" />}
            >
              Explore experts
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 z-10 animate-fade animate-delay-[1500ms]">
            <div className="flex flex-col p-8 items-center justify-center bg-primary-800 bg-opacity-60 backdrop-blur-sm rounded-3xl">
              <p className="text-4xl font-bold text-primary-50">2023</p>
              <p className="text-lg text-primary-200">Founded</p>
            </div>
            <div className="flex flex-col p-8 items-center justify-center bg-primary-800 bg-opacity-60 backdrop-blur-sm rounded-3xl">
              <p className="text-4xl font-bold text-primary-50">4000+</p>
              <p className="text-lg text-primary-200">Experts</p>
            </div>
            <div className="flex flex-col p-8 items-center justify-center bg-primary-800 bg-opacity-60 backdrop-blur-sm rounded-3xl">
              <p className="text-4xl font-bold text-primary-50">2+</p>
              <p className="text-lg text-primary-200">Cities</p>
            </div>
            <div className="flex flex-col p-8 items-center justify-center bg-primary-700 bg-opacity-40 backdrop-blur-sm rounded-3xl">
              <p className="text-4xl font-bold text-primary-50">1000+</p>
              <p className="text-lg text-primary-200">Connections</p>
            </div>
          </div>
        </div>
        <video
          src={getPublicAssetURL("assets/about/video.mp4")}
          poster={getPublicAssetURL("assets/about/poster.mp4")}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute inset-0 w-full h-full bg-black bg-opacity-50 animate-fade duration-500"></div>
      </section>

      <section className="relative w-full">
        <div className="max-w-7xl mx-auto flex flex-col gap-16 items-center px-4 md:px-8 py-16">
          <div className="text-center">
            <h3 className="text-3xl md:text-4xl font-semibold ">
              Not just about services
            </h3>
            <p className="text-lg">We&apos;re about people</p>
          </div>

          <div className="flex flex-col md:flex-row gap-4 w-full">
            <Card className="md:basis-1/3">
              <CardBody className="p-6">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-12 mb-6">
                  <LuSearch className="size-6 text-primary-500" />
                </div>
                <h4 className="text-xl font-semibold mb-2">
                  Curated Excellence
                </h4>
                <p>
                  We carefully select and showcase professionals who stand out
                  in their field.
                </p>
              </CardBody>
            </Card>
            <Card className="md:basis-1/3">
              <CardBody className="p-6">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-12 mb-6">
                  <HiSparkles className="size-6 text-primary-500" />
                </div>
                <h4 className="text-xl font-semibold mb-2">
                  AI-Powered Matching
                </h4>
                <p>
                  Our intelligent algorithms analyze style, expertise, and past
                  success to create perfect client-expert matches.
                </p>
              </CardBody>
            </Card>
            <Card className="md:basis-1/3">
              <CardBody className="p-6">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-12 mb-6">
                  <TbBolt className="size-6 text-primary-500" />
                </div>
                <h4 className="text-xl font-semibold mb-2">
                  Direct Connection
                </h4>
                <p>
                  Connect directly with experts who align with your vision and
                  style.
                </p>
              </CardBody>
            </Card>
          </div>
          <p className="mt-4 max-w-2xl text-center text-xl font-medium">
            &quot;We believe exceptional results come from exceptional
            individuals. We&apos;ve built a platform that celebrates expertise,
            creativity, and the unique talent each professional brings to their
            craft.&quot;
          </p>
        </div>
      </section>

      <section className="relative w-full bg-gradient-to-b from-primary-100 via-primary-50 to-background">
        <DotPattern />
        <div className="max-w-7xl mx-auto flex flex-col gap-16 items-center px-4 md:px-8 py-16">
          <div className="text-center">
            <h3 className="text-3xl md:text-4xl font-semibold ">
              How <span className="text-primary">AI</span> powers perfect
              matches
            </h3>
          </div>

          <div className="flex flex-col gap-4 w-full">
            <Card>
              <CardBody className="p-6 flex items-center flex-row gap-4">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-10 mb-6">
                  <p className="text-primary-500 text-xl font-bold">1</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2">Style Analysis</h4>
                  <p>
                    AI analyzes portfolios and past projects to understand each
                    expert&apos;s unique style and approach.
                  </p>
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="p-6 flex items-center flex-row gap-4">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-10 mb-6">
                  <p className="text-primary-500 text-xl font-bold">2</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2">Smart Matching</h4>
                  <p>
                    Advanced algorithms process client preferences and project
                    requirements to suggest the most compatible experts.
                  </p>
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="p-6 flex items-center flex-row gap-4">
                <div className="bg-primary-100 rounded-full aspect-square flex justify-center items-center size-10 mb-6">
                  <p className="text-primary-500 text-xl font-bold">3</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2">
                    Quality Assurance
                  </h4>
                  <p>
                    Continuous AI monitoring helps maintain high service
                    standards and expert performance.
                  </p>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      <section className="relative w-full bg-primary-800">
        <div className="max-w-7xl mx-auto flex flex-col gap-20 items-center px-4 md:px-8 py-16 pb-24">
          <h3 className="text-4xl text-foreground-100 font-semibold text-center">
            Meet the team
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {teamMembers.map((member, index) => (
              <div key={index} className="flex flex-col items-center">
                <Avatar
                  src={member.image}
                  className="w-32 h-32 mb-4"
                  alt={member.name}
                />
                <h4 className="text-xl font-medium text-center text-foreground-100">
                  {member.name}
                </h4>
                <p className="text-center text-foreground-300">
                  {member.position}
                </p>
                <div className="flex gap-4 mt-4">
                  {member.socials.linkedIn && (
                    <Link href={member.socials.linkedIn} target="_blank">
                      <IoLogoLinkedin className="text-foreground-400 size-5" />
                    </Link>
                  )}
                  {member.socials.twitter && (
                    <Link href={member.socials.twitter} target="_blank">
                      <IoLogoTwitter className="text-foreground-400 size-5" />
                    </Link>
                  )}
                  {member.socials.github && (
                    <Link href={member.socials.github} target="_blank">
                      <IoLogoGithub className="text-foreground-400 size-5" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="relative w-full">
        <div className="max-w-7xl mx-auto flex flex-col gap-20 items-center px-4 md:px-8 py-16 pb-24">
          <div>
            <h3 className="text-4xl font-semibold text-center">
              Become a part
            </h3>
            <p className="text-lg text-center">
              Three ways you can join{" "}
              <span className="text-primary font-bold">tribe</span>, today!
            </p>
          </div>
          <div className="flex flex-col gap-8 md:flex-row">
            {becomePart.map((item) => (
              <Card
                key={item.title}
                className="md:basis-1/3 relative overflow-hidden"
              >
                <div className="relative">
                  <img
                    className="aspect-[1.1] md:aspect-[1] w-full object-cover"
                    alt={item.title}
                    src={item.image}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background"></div>
                </div>
                <div className="p-4 flex flex-col relative z-10">
                  <h4 className="text-3xl font-bold mb-2">{item.title}</h4>
                  <p className="text-sm">{item.description}</p>
                  <Button
                    href={item.url}
                    as={Link}
                    target={item.external ? "_blank" : "_self"}
                    className="mt-8"
                    color="primary"
                    endContent={
                      item.external ? (
                        <LuExternalLink className="size-4" />
                      ) : (
                        <LuArrowRight className="size-4" />
                      )
                    }
                  >
                    {item.buttonText}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
